import { useState, useEffect } from 'react'
import { X, Copy, RefreshCw } from 'lucide-react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import { designTokens } from '@/utils/design-tokens'
import { OrgInviteService } from '@/api/generated/services/OrgInviteService'
import type { OrgInviteResponse } from '@/api/generated/models/OrgInviteResponse'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'
import { formatDateTime } from '@/utils/date-parser'

interface InviteListModalProps {
  isOpen: boolean
  onClose: () => void
}

type InviteStatus = 'completed' | 'expired' | 'valid'

export default function InviteListModal({
  isOpen,
  onClose
}: InviteListModalProps) {
  const [invites, setInvites] = useState<OrgInviteResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null)

  const { showError, showSuccess } = useAlert()
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Fetch invites on modal open
  useEffect(() => {
    if (isOpen && selectedWorkspace) {
      fetchInvites()
    }
  }, [isOpen, selectedWorkspace])

  const fetchInvites = async () => {
    if (!selectedWorkspace) return

    setIsLoading(true)
    try {
      const response = await OrgInviteService.appsOrganizationViewsListOrgInvites({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: 100
      })

      setInvites(response.data || [])
    } catch (error) {
      console.error('Failed to fetch invites:', error)
      handleApiError(error, showError, '초대 목록을 불러오는데 실패했습니다')
    } finally {
      setIsLoading(false)
    }
  }

  const getInviteStatus = (invite: OrgInviteResponse): InviteStatus => {
    if (invite.accepted_at !== null && invite.accepted_at !== undefined) {
      return 'completed'
    }
    if (invite.expired) {
      return 'expired'
    }
    return 'valid'
  }

  const getStatusInfo = (status: InviteStatus) => {
    switch (status) {
      case 'completed':
        return { label: '가입완료', color: 'success' as const }
      case 'expired':
        return { label: '만료', color: 'error' as const }
      case 'valid':
        return { label: '유효', color: 'info' as const }
    }
  }

  const handleCopyLink = async (inviteLink: string) => {
    try {
      await navigator.clipboard.writeText(inviteLink)
      showSuccess('초대 링크가 복사되었습니다')
    } catch (error) {
      console.error('Failed to copy link:', error)
      showError('링크 복사에 실패했습니다')
    }
  }

  const handleResendInvite = async (inviteId: string, email: string) => {
    if (!selectedWorkspace) return

    setResendingInviteId(inviteId)
    try {
      // Re-create invite for the same email
      await OrgInviteService.appsOrganizationViewsCreateOrgInvite({
        requestBody: {
          org_id: selectedWorkspace.org_id,
          emails: [email]
        }
      })

      showSuccess('초대가 재전송되었습니다')
      
      // Refresh invite list
      await fetchInvites()
    } catch (error) {
      console.error('Failed to resend invite:', error)
      handleApiError(error, showError, '초대 재전송에 실패했습니다')
    } finally {
      setResendingInviteId(null)
    }
  }

  const handleClose = () => {
    if (!isLoading && !resendingInviteId) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[900px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              초대목록
            </h2>
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading || resendingInviteId !== null}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity disabled:opacity-50"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div
                className="text-base"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  color: designTokens.colors.gray[4]
                }}
              >
                불러오는 중...
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && invites.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div
                className="text-base"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  color: designTokens.colors.gray[4]
                }}
              >
                초대 내역이 없습니다
              </div>
            </div>
          )}

          {/* Invite List */}
          {!isLoading && invites.length > 0 && (
            <div className="flex flex-col gap-3">
              {/* Table Header */}
              <div
                className="grid grid-cols-[2fr_1.5fr_1fr_2fr] gap-4 px-4 py-3 rounded"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  borderBottom: `1px solid ${designTokens.colors.gray[1]}`
                }}
              >
                <div
                  className="font-semibold text-sm"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.gray[6]
                  }}
                >
                  이메일
                </div>
                <div
                  className="font-semibold text-sm"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.gray[6]
                  }}
                >
                  초대일시
                </div>
                <div
                  className="font-semibold text-sm"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.gray[6]
                  }}
                >
                  상태
                </div>
                <div
                  className="font-semibold text-sm text-center"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.gray[6]
                  }}
                >
                  작업
                </div>
              </div>

              {/* Table Rows */}
              {invites.map((invite) => {
                const status = getInviteStatus(invite)
                const statusInfo = getStatusInfo(status)
                const isCompleted = status === 'completed'
                const isResending = resendingInviteId === invite.id

                return (
                  <div
                    key={invite.id}
                    className="grid grid-cols-[2fr_1.5fr_1fr_2fr] gap-4 px-4 py-3 rounded hover:bg-gray-50 transition-colors"
                    style={{
                      borderBottom: `1px solid ${designTokens.colors.gray[1]}`
                    }}
                  >
                    {/* Email */}
                    <div
                      className="text-sm flex items-center"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        color: designTokens.colors.gray[6]
                      }}
                    >
                      {invite.email}
                    </div>

                    {/* Created At */}
                    <div
                      className="text-sm flex items-center"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        color: designTokens.colors.gray[5]
                      }}
                    >
                      {formatDateTime(invite.created_at)}
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      <Tag color={statusInfo.color} size="small">
                        {statusInfo.label}
                      </Tag>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-center gap-2">
                      {/* Copy Link Button */}
                      <Button
                        size="small"
                        color="white"
                        onClick={() => handleCopyLink(invite.invite_link)}
                        disabled={status === 'completed' || status === 'expired'}
                        className="flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" strokeWidth={2} />
                        링크복사
                      </Button>

                      {/* Resend Button */}
                      <Button
                        size="small"
                        color="white"
                        onClick={() => handleResendInvite(invite.id, invite.email)}
                        disabled={status === 'completed' || isResending}
                        className="flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" strokeWidth={2} />
                        {isResending ? '재전송 중...' : '재초대'}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
